<template>
  <div>
    <b-container class="container-panel">
      <b-row align-v="end">
        <b-col cols="3">
          <img src="./assets/images/logoSmall.jpg"
               class="logo-margin"
               alt="Ambit Cares Logo"
               aria-label="Ambit Cares Logo" />
        </b-col>
        <b-col cols="9">
        </b-col>
      </b-row>
    </b-container>
    <b-container class="gray-tall-panel">
      <b-row align-v="end">
        <b-col cols="12">
          <span>{{this.headingDonorNote}}</span>
        </b-col>
      </b-row>
    </b-container>
    <b-container class="container-panel">
      <b-row>
        <b-col cols="12">
          <b-alert id="validationErrorsAlert"
                   v-model="alerts.validationErrors"
                   variant="danger"
                   dismissible>
            Please fix any errors on the form!
          </b-alert>
        </b-col>
      </b-row>
    </b-container>

    <!-- Main blocks -->
    <b-container class="container-panel outlined-panel">
      <b-row>
        <b-col class="container-panel-heading">
          Billing Information
        </b-col>
      </b-row>

      <b-form>
        <b-container fluid>
          <b-row>
            <b-col>
              <b-form-group id="nameOnCard-group">
                <b-form-input id="nameOnCardField"
                              v-model="billing.fullName"
                              v-validate="{ required: true, regex: nameRegex }"
                              :state="validateState('nameOnCardField')"
                              name="nameOnCardField"
                              placeholder="Name as it appears on your card" />
                <b-form-invalid-feedback id="nameOnCardField-live-feedback">
                  Required field, may not contain special characters
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-form-group id="addressLine1-group">
                <b-form-input id="addressLine1Field"
                              v-model="billing.addressLine1"
                              v-validate="'required'"
                              :state="validateState('addressLine1Field')"
                              name="addressLine1Field"
                              placeholder="Street Address" />
                <b-form-invalid-feedback id="addressLine1Field-live-feedback">
                  Required field
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="8">
              <b-form-group id="addressLine2-group">
                <b-form-input id="addressLine2Field"
                              v-model="billing.addressLine2"
                              :state="validateState('addressLine2Field')"
                              name="addressLine2Field"
                              placeholder="Street Address Line 2" />
              </b-form-group>
            </b-col>
            <b-col cols="4">
              <b-form-group id="phoneNumber-group">
                <b-form-input id="phoneNumberField"
                              v-model="billing.phone"
                              v-validate="{required: true, regex: phoneRegex}"
                              :state="validateState('phoneNumberField')"
                              name="phoneNumberField"
                              type="text"
                              placeholder="Phone Number" />
                <b-form-invalid-feedback id="phoneNumberField-live-feedback">
                  Enter a phone number, possibly with a country code preceded by "+"
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="6">
              <b-form-group id="city-group">
                <b-form-input id="cityField"
                              v-model="billing.city"
                              v-validate="'required'"
                              :state="validateState('cityField')"
                              name="cityField"
                              placeholder="City" />
                <b-form-invalid-feedback id="cityField-live-feedback">
                  Required field
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col cols="3">
              <b-form-group id="state-group">
                <b-form-select id="stateField"
                               v-model="billing.state"
                               v-validate="'required'"
                               :state="validateState('stateField')"
                               :options="listOfStates"
                               name="stateField"
                               placeholder="State" />
                <b-form-invalid-feedback id="stateField-live-feedback">
                  Required field
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col cols="3">
              <b-form-group id="zipCode-group">
                <b-form-input id="zipCodeField"
                              v-model="billing.zipCode"
                              v-validate="{ required: true, regex: postalCodeRegex }"
                              :state="validateState('zipCodeField')"
                              :formatter="formatToUpperCase"
                              name="zipCodeField"
                              type="text"
                              placeholder="Zip/Postal Code"
                              title="A 5 digit US Zipcode or an international postal code"
                              lazy-formatter />
                <b-form-invalid-feedback id="zipCodeField-live-feedback">
                  Required field: A 5-digit US Zipcode or an international postal code
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="4">
              <b-form-group id="email1-group">
                <b-form-input id="email1Field"
                              ref="email1Field"
                              v-model="billing.email1"
                              v-validate="'required|email'"
                              :state="validateState('email1Field')"
                              name="email1Field"
                              placeholder="Email address" />
                <b-form-invalid-feedback id="email1Field-live-feedback">
                  Required field
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col cols="4">
              <b-form-group id="email2-group">
                <b-form-input id="email2Field"
                              v-model="billing.email2"
                              v-validate="'required|email|confirmed:email1Field'"
                              data-vv-as="email1Field"
                              :state="validateState('email2Field')"
                              name="email2Field"
                              placeholder="Confirm email" />
                <b-form-invalid-feedback id="email2Field-live-feedback">
                  The two email fields must match
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col cols="4"
                   class="checkbox-cell">
              <b-form-checkbox id="emailCheckbox"
                               v-model="billing.emailReceipt"
                               name="emailCheckbox">
                Email my receipt
              </b-form-checkbox>
            </b-col>
          </b-row>
        </b-container>
      </b-form>
    </b-container>
    <b-container class="container-panel outlined-panel">
      <b-row>
        <b-col class="container-panel-heading">
          Credit Card Information
        </b-col>
      </b-row>
      <b-container>
        <b-row>
          <b-col class="align-left credit-card-col">
            <font-awesome-icon id="visa-card"
                               :icon="['fab', 'cc-visa']"
                               size="3x"
                               :style="{color: visaCardColor}">
            </font-awesome-icon>&nbsp;
            <font-awesome-icon id="mastercard-card"
                               :icon="['fab', 'cc-mastercard']"
                               size="3x"
                               :style="{color: mastercardCardColor}">
            </font-awesome-icon>&nbsp;
            <font-awesome-icon id="amex-card"
                               :icon="['fab', 'cc-amex']"
                               size="3x"
                               :style="{color: amexCardColor}">
            </font-awesome-icon>&nbsp;
            <font-awesome-icon id="discover-card"
                               :icon="['fab', 'cc-discover']"
                               size="3x"
                               :style="{color: discoverCardColor}">
            </font-awesome-icon>&nbsp;
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <b-form-group id="cardNumber-group">
              <b-form-input id="cardNumberField"
                            v-model="creditCard.numberMasked"
                            v-validate="{ required: true, creditcard: {unmasked : this.creditCard.numberActual } }"
                            :state="validateState('cardNumberField')"
                            name="cardNumberField"
                            type="text"
                            placeholder="Enter Card Number"
                            @focus.native="creditCardFocus"
                            @blur.native="creditCardBlur"
                            @input.native="selectCreditCardImageToHighlight" />
              <b-form-invalid-feedback id="cardNumberField-live-feedback">
                Required: Enter a valid credit card number
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <b-form-group id="cardExpMonth-group"
                          label="Card Expiration Month:"
                          label-for="cardExpMonthField">
              <b-form-select id="cardExpMonthField"
                             v-model="creditCard.expMonth"
                             v-validate="'required'"
                             :state="validateState('cardExpMonthField')"
                             :options="possibleExpirationMonths"
                             name="cardExpMonthField"
                             placeholder="Card Expiration Month" />
              <b-form-invalid-feedback id="cardExpMonthField-live-feedback">
                Required field
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group id="cardExpYear-group"
                          label="Card Expiration Year:"
                          label-for="cardExpYearField">
              <b-form-select id="cardExpYearField"
                             v-model="creditCard.expYear"
                             v-validate="'required'"
                             :state="validateState('cardExpYearField')"
                             :options="possibleExpirationYears"
                             name="cardExpYearField"
                             placeholder="Card Expiration Year" />
              <b-form-invalid-feedback id="cardExpYearField-live-feedback">
                Required field
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group id="cardCVV-group"
                          label="CVV:"
                          label-for="cardCVVField">
              <b-form-input id="cardCVVField"
                            v-model="creditCard.cvvMasked"
                            v-validate="{required: true, max:4, regex: cvvRegex }"
                            :state="validateState('cardCVVField')"
                            name="cardCVVField"
                            type="text"
                            placeholder="1234"
                            title="The 3 or 4 digit Card Verification Value (CVV) number"
                            @focus.native="cvvFocus"
                            @blur.native="cvvBlur" />
              <b-form-invalid-feedback id="cardCVVField-live-feedback">
                Required field: a three or four digit number
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>
      </b-container>
    </b-container>
    <b-container class="container-panel outlined-panel">
      <b-row>
        <b-col class="container-panel-heading">
          Donation
        </b-col>
      </b-row>

      <b-form>
        <b-container fluid>
          <b-row>
            <b-col cols="9"
                   class="checkbox-cell">
              <b-form-checkbox id="authorizeCheckbox"
                               v-model="authorizationCheck"
                               name="authorizeCheckbox"
                               :required="true"
                               @change="alerts.authorizeUnchecked=false">
                I authorize Ambit Energy to charge my credit/debit card for the specified amount
              </b-form-checkbox>

            </b-col>
            <b-col cols="3">
              <b-form-group id="billingAmount-group"
                            label="Amount to Donate (USD):"
                            label-for="billingAmountField">
                <b-form-input id="billingAmountField"
                              v-model="donationAmount"
                              v-validate="'required|min_value:1.00|max_value:1500.00'"
                              :state="validateState('billingAmountField')"
                              :disabled="disableAmountField"
                              name="billingAmountField"
                              type="number"
                              placeholder="Amount (US$)" />
                <b-form-invalid-feedback id="billingAmountField-live-feedback">
                  Required: Enter a number between 1 and 1500
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>
          <b-alert id="authorizeUncheckedAlert"
                   v-model="alerts.authorizeUnchecked"
                   dismissible>
            You must authorize Ambit Energy to process your credit card to submit this donation!
          </b-alert>
        </b-container>
      </b-form>
    </b-container>

    <b-container class="container-panel">
      <b-row align-v="end">
        <b-col cols="6">
        </b-col>
        <b-col cols="6">
          <b-button class="btn btn-primary btn-lg btn-margin-narrow-aligned-right float-right"
                    title="Submit the donation"
                    :disabled="disableSubmitBtn"
                    aria-label="Submit the donation"
                    @click="onSubmit">
            Submit Donation
          </b-button>

          <b-button class="btn btn-primary btn-lg btn-margin-narrow-aligned-right float-right"
                    title="Clear the data from the form"
                    aria-label="Clear the data from the form"
                    @click="wipeOutAllInfo">
            Clear Form
          </b-button>

        </b-col>
      </b-row>
    </b-container>

    <!-- The various modal windows -->
    <!-- Shown before the user commits to doing the donation - to confirm intent -->
    <b-modal id="confirmModal"
             ref="confirmModal"
             v-model="modals.confirmModalShown"
             aria-labelledby="Confirmation"
             title="Are you sure?"
             @ok="this.processDonation">
      <b-container>
        <b-row>
          <b-col cols="2"
                 style="color: blue">
            <font-awesome-icon icon="question-circle"
                               size="3x">
            </font-awesome-icon>
          </b-col>
          <b-col cols="9">
            <span class="modal-text">You are about to process a donation of ${{this.donationAmount}}.  Please click OK if this is correct</span><br />
          </b-col>
        </b-row>
      </b-container>
    </b-modal>

    <!-- Shown after a failure during the donation submission /post -->
    <b-modal id="errorResponseModal"
             ref="errorResponseModal"
             v-model="modals.errorResponseModalShown"
             aria-labelledby="errorResponseModal"
             title="Error Submitting Data"
             ok-only>
      <b-container>
        <b-row>
          <b-col cols="2"
                 style="color: red">
            <font-awesome-icon icon="exclamation-circle"
                               size="3x">
            </font-awesome-icon>
          </b-col>
          <b-col cols="10">
            <span class="modal-head">Transaction failed</span><br />
            <table>
              <tr>
                <td class="modal-note">Error Code: </td>
                <td class="modal-data">{{ modals.errorRes }}</td>
              </tr>
            </table>
            <span class="modal-note">Please try again</span>
          </b-col>

        </b-row>
      </b-container>
    </b-modal>

    <!-- Shown after a failure during the donation submission /post -->
    <b-modal id="busyModal"
             ref="busyModal"
             v-model="modals.busyModalShown"
             aria-labelledby="busyModal"
             :cancel-disabled="true"
             :hide-header="true"
             title="Submitting your donation"
             ok-only>
      <b-container>
        <b-row>
          <b-col cols="12"
                 style="color: #F15F1F">
            <b-spinner></b-spinner>
            <span style="color: black;">&nbsp;&nbsp;Busy submitting your donation</span>
          </b-col>
        </b-row>
      </b-container>
    </b-modal>

  </div>
</template>

<script>

  import utilities from '../utilities';
  import constants from '../constants';
  
  export default {
    name: 'DonationWithCardScan',
    data() {
      return {
        rawCardInput: '',
        disableSubmitBtn: false,
        disableAmountField: false,

        donor: {
          fullName: '',
          idNumber: '',
          category: '',      // Consultant, Customer, ...
          locationCode: '',
          zipCode: '',
        },

        billing: {
          fullName: '',
          addressLine1: '',
          addressLine2: '',
          city: '',
          state: '',
          zipCode: '',
          email1: '',
          email2: '',
          emailReceipt: true,
          phone: '',
        },

        creditCard: {
          numberMasked: '',
          numberActual: '',
          expMonth: '',
          expYear: '',
          cvvMasked: '',
          cvvActual: '',
          cardType: '',
        },

        modals: {
          errorRes: '',
          errorResponseModalShown: false,
          confirmModalShown: false,
          busyModalShown: false,
          confirmMessage: '',
          confirmTitle: '',
        },

        alerts: {
          validationErrors: false,
          authorizeUnchecked: false,
        },

        donationAmount: 0.0,

        nameRegex: constants.nameRegex,
        cvvRegex: constants.cvvRegex,
        phoneRegex: constants.phoneRegex,
        postalCodeRegex: constants.postalCodeRegex,

        listOfStates: utilities.loadStates(),
        possibleExpirationMonths: utilities.loadExpiryMonths(),
        possibleExpirationYears: utilities.loadExpiryYears(),

        authorizationCheck: false,

        visaCardColor: constants.initialCardColor,
        mastercardCardColor: constants.initialCardColor,
        amexCardColor: constants.initialCardColor,
        discoverCardColor: constants.initialCardColor,

      }
    },

    computed: {
      headingDonorNote() {
        if (['Consultant', 'Customer'].indexOf(this.donor.category) >= 0) {
          return `${this.donor.fullName} / ${this.donor.category}: ${this.donor.idNumber}`;
        }

        // otherwise
        return `${this.donor.fullName} / ${this.donor.category}`;
      },
    },

    // -----------------------------------------------------------------------
    // The created method is part of the Vue.js lifecycle.
    // It gets called as the page is created. 
    // -----------------------------------------------------------------------
    created() {
      this.wipeOutAllInfo();
      this.$store.commit('clearReceiptData');
      const transferedData = this.$store.state.initialDonationData;
      this.donationAmount = transferedData.amount;
      this.disableAmountField = true;

      this.donor.fullName = transferedData.fullName;
      this.donor.category = transferedData.donorType;
      this.donor.idNumber = transferedData.donorNumber;
      this.donor.locationCode = transferedData.locationCode;
      this.billing.fullName = transferedData.fullName;
      this.billing.addressLine1 = transferedData.address1;
      this.billing.addressLine2 = transferedData.address2;
      this.billing.city = transferedData.city;
      this.billing.state = transferedData.state;
      this.billing.zipCode = transferedData.zipCode;
    },

    methods: {

      cvvFocus() {
        this.creditCard.cvvMasked = utilities.unmaskField(this.creditCard.cvvActual, this.creditCard.cvvMasked);
      },

      cvvBlur() {
        [this.creditCard.cvvActual, this.creditCard.cvvMasked] = utilities.maskCvv(this.creditCard.cvvActual, this.creditCard.cvvMasked);
      },

      creditCardFocus() {
        this.creditCard.numberMasked = utilities.unmaskField(this.creditCard.numberActual, this.creditCard.numberMasked);
      },

      creditCardBlur() {
        [this.creditCard.numberActual, this.creditCard.numberMasked] = utilities.maskCreditCard(
          this.creditCard.numberActual,
          this.creditCard.numberMasked,
        );
      },

      selectCreditCardImageToHighlight(arg) {
        this.visaCardColor = arg;
        this.visaCardColor = constants.dimmedCardColor;
        this.mastercardCardColor = constants.dimmedCardColor;
        this.amexCardColor = constants.dimmedCardColor;
        this.discoverCardColor = constants.dimmedCardColor;
        if (this.creditCard.numberMasked.length > 2) {
          const firstChar = this.creditCard.numberMasked[0];
          switch (firstChar) {
            case '4':
              this.visaCardColor = constants.selectedCardColor;
              this.creditCard.cardType = 'Visa';
              break;
            case '2':
            case '5':
              this.mastercardCardColor = constants.selectedCardColor;
              this.creditCard.cardType = 'MasterCard';
              break;
            case '6':
              this.discoverCardColor = constants.selectedCardColor;
              this.creditCard.cardType = 'Discover';
              break;
            default:
              this.amexCardColor = constants.selectedCardColor;
              this.creditCard.cardType = 'American Express';
              break;
          }
        }
      },

      // -----------------------------------------------------------------------
      // this is called by every field as part of the live validation process
      // Returns one of:
      //    false:  shows a red mark
      //    true:   shows a green mark
      //    null:   no mark shown
      // -----------------------------------------------------------------------
      validateState(ref) {
        // if the field exists
        if (this.veeFields[ref]
          // and is either "dirty" or "validated" (i.e., touched)
          && (this.veeFields[ref].dirty || this.veeFields[ref].validated)) {
          // return an indicator if it's good (true) or bad (false)
          return !this.veeErrors.has(ref);
        }
        // otherwise - it hasn't been touched, so leave it alone
        return null;
      },

      // -----------------------------------------------------------------------
      // Canadian postal codes look like "A9A 9A9" (alternationg alpha and numberics,
      // in two groups of three, upper case)
      // Make sure that the entered code gets converted to upper case.
      // Also used to upper-case the C-Number field
      // -----------------------------------------------------------------------
      formatToUpperCase(value) {
        return value.toUpperCase();
      },

      wipeOutBillingInfo() {
        this.billing.fullName = '';
        this.billing.addressLine1 = '';
        this.billing.addressLine2 = '';
        this.billing.phone = '';
        this.billing.city = '';
        this.billing.state = '';
        this.billing.zipCode = '';
        this.billing.email1 = '';
        this.billing.email2 = '';
        this.billing.emailReceipt = true;
      },

      wipeOutCreditCardInfo() {
        this.creditCard.numberActual = '';
        this.creditCard.numberMasked = '';
        this.creditCard.expMonth = '';
        this.creditCard.expYear = '';
        this.creditCard.cvvActual = '';
        this.creditCard.cvvMasked = '';
        this.visaCardColor = constants.initialCardColor
        this.mastercardCardColor = constants.initialCardColor
        this.amexCardColor = constants.initialCardColor
        this.discoverCardColor = constants.initialCardColor
      },

      wipeOutAllInfo() {
        this.wipeOutBillingInfo();
        this.wipeOutCreditCardInfo();
        // clean up all the "required field" errors that will fire when the fields are cleared
        this.veeErrors.clear();
        this.$validator.reset();
        // re-enable the submit button if it got stuck disabled
        // this.disableSubmitBtn = false;
        window.scrollTo({ top: 0, behavior: 'smooth' });
        this.creditCard.authorizationCheck = false;
      },

      onSubmit() {
        this.alerts.validationErrors = false;
        const self = this;
        if (!this.authorizationCheck) {
          this.alerts.authorizeUnchecked = true;
          return;
        }
        self.disableSubmitBtn = true;

        self.$validator.validateAll().then(
          (result) => {
            if (result) {
              // this will show the confirmation modal window, if the user presses OK, then ProcessDonation will be called
              self.$refs.confirmModal.show();
              window.scrollTo({ top: 0, behavior: 'smooth' });
              self.disableSubmitBtn = false;
            } else {
              this.alerts.validationErrors = true;
              self.disableSubmitBtn = false;
            }
          },
        );
      },

      processDonation() {
        const url = this.getPostUrl();
        const self = this;
        self.$refs.busyModal.show();
        this.donor.fullName = this.donor.fullName || `${this.donor.firstName} ${this.donor.lastName}`;

        // remove dashes, parentheses and spaces from the phone number (leave in any leading "+", though)
        const phoneRegex = new RegExp(/[- ()]/g);
        const phoneNum = this.billing.phone.replace(phoneRegex, '');

        // Note that a "donor number" is required for the transaction, but there is no "donor number"
        // for friends or employees.  So, we populate that "donor number" field with the Employee's
        // or friend's full name instead.
        let donorIdNumber = this.donor.idNumber;
        if (!donorIdNumber) {
          donorIdNumber = this.donor.fullName;
        }

        const postData = {
          BillAmount: this.donationAmount,
          BillingAddress: this.billing.addressLine1,
          BillingAddress2: this.billing.addressLine2,
          BillingCity: this.billing.city,
          BillingFullName: this.billing.fullName,
          BillingPhoneNumber: phoneNum,
          BillingState: this.billing.state,
          BillingZip: this.billing.zipCode,
          CreditCardNumber: this.creditCard.numberActual,
          DonorNumber: donorIdNumber,
          DonorType: this.donor.category,
          EmailAddress: this.billing.email1,
          EntityType: 'Individual',
          ExpiryMonth: this.creditCard.expMonth,
          ExpiryYear: this.creditCard.expYear,
          IpAddress: '',
          LocationCode: this.donor.locationCode,
          SecurityCode: this.creditCard.cvvActual,
          SendReceiptEmail: this.billing.emailReceipt,
        };

        this.axios.post(url, postData)
          .then((res) => {
            if (res.status === 200) {
              if (!res.data.isError) {
                // the HAPPY PATH
                this.gotoReceiptPage(res.data);
                self.wipeOutAllInfo();
              } else {
                // if we get a 200, but we still get isError true...
                self.modals.errorRes = res.data.message || res.data.ResponseMessage;
                self.closeBusyWindow();
                self.$refs.errorResponseModal.show();
              }
            } else if (res.status === 400 || res.status === 500) {
              self.closeBusyWindow();
              self.modals.errorRes = res.data.message || res.data.ResponseMessage;
              self.$refs.errorResponseModal.show();
            } else {
              self.closeBusyWindow();
              self.modals.errorRes = res.data.message || res.data.ResponseMessage;
              self.$refs.errorResponseModal.show();
            }
            self.disableSubmitBtn = false;
            self.closeBusyWindow();
          }).catch((ex) => {
            self.modals.errorRes = `Unexpected error processing payment: ${ex.message}`;
            self.closeBusyWindow();
            self.$refs.errorResponseModal.show();
            self.disableSubmitBtn = false;
          });
      },

      gotoReceiptPage(resultData) {
        this.$store.commit('setReceiptData', {
          donorType: this.donor.category,
          donorNumber: this.donor.idNumber,
          donorFullName: this.donor.fullName,
          creditCardTransactionID: resultData.data.TransactionID,
          billingFullName: this.billing.fullName,
          creditCardNumberMasked: this.creditCard.numberMasked,
          creditCardTransactionDate: resultData.data.TransactionDate,
          creditCardAmount: this.donationAmount,
          creditCardType: this.creditCard.cardType,
          emailAddress: this.billing.email1,
          showEmailMessage: this.billing.emailReceipt,
          creditCardTransactionStatus: resultData.data.Status,
          creditCardReferenceNumber: resultData.data.ReferenceNumber,
        });
        this.$router.push({ name: 'Receipt' });
      },

      getPostUrl() {
        // at one point, this looked up the environment (with location.hostname)
        // and returned a URL.  Right now, it always just returns...
        const endpointUri = '/api/donordonation/makedonationpayment';
        return endpointUri;
      },

      closeBusyWindow() {
        this.$refs.busyModal.hide();
        this.busyModalShown = false;
      },

    },
  }

</script>

<style src="./assets/css/CommonVue.css" scoped>
</style>

<style scoped>
</style>
